<template>
  <div class="min-h-screen bg-gray-50 px-4 pt-10 pb-20">
    <!-- Main checkout form -->
    <div class="max-w-2xl mx-auto pb-20 px-4 transition-opacity duration-300">
      <!-- Header -->
      <div class="flex items-center gap-4 mb-10">
        <button
          @click="handleBack"
          class="flex p-3 hover:bg-gray-100 rounded-full transition-colors text-gray-600 border border-transparent hover:border-gray-200"
        >
          <ArrowLeft :size="24" />
        </button>
        <div class="flex-1">
          <h1 class="text-4xl font-serif font-bold text-gray-900">
            {{ isAddItemsMode ? "Submit Order" : "Your Check" }}
          </h1>
          <p
            v-if="isYourCheckMode && currentOrderId"
            class="text-sm text-gray-500 mt-1"
          >
            Order #{{ currentOrderId }}
          </p>
        </div>
      </div>

      <!-- Main card container -->
      <div
        class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
        style="box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);"
      >
        <div class="p-8 md:p-10 space-y-10">
          <!-- Order information section -->
          <CheckoutOrderInfoSection
            :cart-items="displayItems"
            :errors="errors"
            :grand-total="grandTotal"
            :hide-order-type="true"
          />

          <!-- Summary and submit action -->
          <div class="pt-6 border-t border-gray-100 space-y-6">
            <div class="flex flex-col gap-3 text-gray-600">
              <div class="flex justify-between">
                <span>Sub Total</span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(subtotal) }}
                </span>
              </div>
              <div class="flex justify-between" v-if="tipAmount">
                <span>Tip</span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(tipAmount) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span>Tax</span>
                <span class="font-medium text-gray-900">
                  {{ formatPrice(getTotalTax) }}
                </span>
              </div>
              <div
                class="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t border-gray-100 mt-2"
              >
                <span>Total</span>
                <span>{{ formatPrice(grandTotal) }}</span>
              </div>
            </div>

            <button
              v-if="isAddItemsMode"
              @click="handleSubmit"
              :disabled="isSubmitting"
              :class="[
                'w-full text-white font-bold py-5 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform active:scale-95 flex justify-center items-center gap-2 disabled:cursor-not-allowed',
                !isSubmitting
                  ? 'bg-primary-600 hover:bg-primary-700'
                  : 'bg-gray-300 disabled:shadow-none'
              ]"
              :style="
                !isSubmitting
                  ? 'box-shadow: 0 20px 25px rgba(37, 99, 235, 0.2);'
                  : ''
              "
            >
              <span v-if="isSubmitting" class="flex items-center gap-2">
                <Loader2 :size="24" class="animate-spin" /> Processing Order...
              </span>
              <span v-else class="text-xl">
                <span v-if="currentOrderId">Add Items to Order</span>
                <span v-else>Submit Order to Staff</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CheckoutOrderInfoSection from "@/components/Checkout/CheckoutOrderInfoSection/index.vue";
import getStoreInfo, {
  createTableOrder,
  decodeTableHash
} from "@/helpers/apiHelper";
import liveObjectsService from "@/helpers/liveObjectsService";
import { ArrowLeft, Loader2 } from "lucide-vue";
import { calculateGrandTotal } from "@/helpers/orderHelpers";
import { resolveStoreAlias } from "@/helpers/urlHelper";

export default {
  name: "TableCheckout",
  components: {
    CheckoutOrderInfoSection,
    ArrowLeft,
    Loader2
  },
  data() {
    return {
      isSubmitting: false,
      tipAmount: 0,
      errors: {},
      localTableId: null,
      storeInfo: null
    };
  },
  computed: {
    ...mapGetters([
      "getStore",
      "getCurrencySymbol",
      "formatPrice",
      "dining_option"
    ]),
    currency() {
      return this.getCurrencySymbol || "$";
    },
    orderConfig() {
      return (
        this.$store.getters["tableOrder/getOrderConfig"] || {
          type: "dine_in",
          timing: "asap"
        }
      );
    },
    cartItems() {
      return this.$store.getters["tableOrder/getCartItems"] || [];
    },
    newItems() {
      return this.$store.getters["tableOrder/getNewItems"] || [];
    },
    sentItems() {
      return this.$store.getters["tableOrder/getSentItems"] || [];
    },
    currentOrderId() {
      return this.$store.getters["tableOrder/getCurrentOrderId"];
    },
    existingOrder() {
      return this.$store.getters["tableOrder/getExistingOrder"];
    },
    isAddItemsMode() {
      return this.newItems.length > 0;
    },
    isYourCheckMode() {
      return this.newItems.length === 0 && this.sentItems.length > 0;
    },
    displayItems() {
      return this.isAddItemsMode ? this.newItems : this.cartItems;
    },
    storeAlias() {
      return resolveStoreAlias(this.$route);
    },
    tableHash() {
      return this.$route.params.tableHash;
    },
    tableId() {
      return this.$store.getters["tableOrder/getTableId"] || this.localTableId;
    },
    subtotal() {
      if (this.isAddItemsMode) {
        return this.$store.getters["tableOrder/newItemsSubtotal"] || 0;
      } else {
        return this.$store.getters["tableOrder/subtotal"] || 0;
      }
    },
    getTotalTax() {
      if (this.isAddItemsMode) {
        return this.$store.getters["tableOrder/newItemsTax"] || 0;
      } else {
        return this.$store.getters["tableOrder/getTotalTax"] || 0;
      }
    },
    grandTotal() {
      const grand = calculateGrandTotal(
        this.subtotal,
        this.getTotalTax,
        this.isStoreTaxInclusive
      );
      return grand + this.tipAmount;
    },
    isStoreTaxInclusive() {
      const store = this.$store.getters.getStore;
      return (
        store?.tax_option === "inclusive" ||
        this.$store.getters.isStoreTaxInclusive === true
      );
    }
  },
  async mounted() {
    window.scrollTo(0, 0);
    await this.initializeTableInfo();

    if (!this.cartItems || this.cartItems.length === 0) {
      if (this.storeAlias && this.tableHash) {
        this.$router.push({
          name: "TableOrder",
          params: {
            storeAlias: this.storeAlias,
            tableHash: this.tableHash
          }
        });
      }
    }

    // Initialize Ably subscription using centralized store module
    if (this.currentOrderId) {
      await this.$store.dispatch("tableOrder/initializeAbly", {
        onOrderUpdate: this.handleAblyOrderUpdate
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    // TableOrder <-> TableCheckout is one continuous ordering session (every
    // round: browse -> checkout -> back to menu). Tearing down the Ably
    // connection on every hop between them closes a real-time window where a
    // staff-side edit (e.g. added via POS) can land in that gap and never
    // reach this tab until a manual refresh. Only clean up when actually
    // leaving the table-ordering flow.
    if (to.name !== "TableOrder" && to.name !== "TableCheckout") {
      this.$store.dispatch("tableOrder/cleanupAbly");
    }
    next();
  },
  methods: {
    // Shared with the initializeAbly call in submitOrder() - a customer who
    // places their first order here has no subscription until that call fires
    // (the one in mounted() only runs when currentOrderId already exists), so
    // both call sites need the same redirect-on-close/cancel behavior.
    handleAblyOrderUpdate(notification) {
      if (
        notification.type === "order_closed" ||
        notification.type === "order_cancelled"
      ) {
        // Redirect back to menu when order is closed or cancelled by staff
        if (this.storeAlias && this.tableHash) {
          this.$router.push({
            name: "TableOrder",
            params: {
              storeAlias: this.storeAlias,
              tableHash: this.tableHash
            }
          });
        }
      }
    },
    handleBack() {
      if (this.storeAlias && this.tableHash) {
        this.$router.push({
          name: "TableOrder",
          params: {
            storeAlias: this.storeAlias,
            tableHash: this.tableHash
          }
        });
      }
    },
    validateForm() {
      this.errors = {};
      return true;
    },
    hasUnavailable86dItems() {
      const isProduct86d = this.$store.getters["store/isProduct86d"];
      return (this.newItems || []).some((item) =>
        isProduct86d?.(item.id || item.product?.id)
      );
    },
    async initializeTableInfo() {
      const tableHash = this.tableHash;

      if (!this.tableId && tableHash) {
        try {
          const storeInfoResponse = await getStoreInfo(this.storeAlias);

          if (storeInfoResponse.status === 404) {
            await this.$router.push({ name: "NotFound" });
            return;
          }

          if (!storeInfoResponse.ok) {
            throw new Error("Failed to fetch store information");
          }

          this.storeInfo = await storeInfoResponse.json();

          if (this.storeInfo.dashboard_link) {
            await this.$store.dispatch(
              "setBaseUrl",
              this.storeInfo.dashboard_link
            );
          }

          const decodeResponse = await decodeTableHash(tableHash);
          if (decodeResponse.data) {
            this.localTableId = decodeResponse.data.table_id;
            await this.$store.dispatch("tableOrder/setTableInfo", {
              tableId: decodeResponse.data.table_id,
              tableName: decodeResponse.data.table_name,
              tableHash: tableHash
            });
          }
        } catch (error) {
          console.error(
            "[TableCheckout] Error initializing table info:",
            error
          );
        }
      }
    },
    /**
     * Publish order update to LiveObjects so POS sees it in real-time
     * Uses LiveObjects map.set operation which biyowebpos subscribes to
     * @param {Object} order - The order object from API
     * @param {Object} diningOptionOverride - Optional override for dining option {name, id}
     */
    async publishOrderToLiveObjects(order, diningOptionOverride = null) {
      try {
        // 1. Get existing order from LiveObjects to preserve any unsaved POS items
        let existingLiveOrder = null;
        try {
          existingLiveOrder = await liveObjectsService.getOrderFromMap(
            order.id
          );
        } catch (e) {
          console.warn(
            "[TableCheckout] Failed to get existing LiveObjects order:",
            e
          );
        }

        const now = new Date().toISOString();
        // CRITICAL: table must be an integer for biyowebpos comparison (order.table === table.id)
        const tableId = parseInt(order.table || this.tableId, 10);

        // Prepare items - start with API items (authoritative for saved state)
        // Ensure we clone the array to avoid mutating the original prop
        const mergedItems = order.items ? [...order.items] : [];

        let preservedSubtotal = 0;
        let preservedTax = 0;

        // If we have an existing LiveObjects order, merge in any unsaved items (items without ID)
        if (
          existingLiveOrder &&
          existingLiveOrder.items &&
          Array.isArray(existingLiveOrder.items)
        ) {
          const apiItemIds = new Set(
            mergedItems.map((i) => i.id).filter((id) => !!id)
          );
          const apiItemLocalIds = new Set(
            mergedItems.map((i) => i.local_id).filter((id) => !!id)
          );

          existingLiveOrder.items.forEach((existingItem) => {
            // Check if this item exists in the API response (by ID or local_id)
            // We use loose comparison for IDs to handle potential string/number mismatches
            const existsInApi =
              (existingItem.id && apiItemIds.has(existingItem.id)) ||
              (existingItem.local_id &&
                apiItemLocalIds.has(existingItem.local_id));

            // If item exists in LiveObjects but not in API response, preserve it!
            if (!existsInApi) {
              mergedItems.push(existingItem);

              // Calculate totals for this preserved item
              const qty = parseFloat(existingItem.quantity) || 1;
              const price = parseFloat(existingItem.price) || 0;
              const mods = existingItem.modifiers
                ? existingItem.modifiers.reduce(
                    (s, m) => s + (parseFloat(m.price) || 0),
                    0
                  )
                : 0;
              // Assuming item.tax is per unit or we use tax_total if available
              // live-staging logic used item.tax * qty if simple tax
              const tax = parseFloat(existingItem.tax) || 0;

              preservedSubtotal += (price + mods) * qty;
              preservedTax += tax * qty;
            }
          });
        }

        // Initialize totals from API response
        let finalSubtotal = parseFloat(order.subtotal) || 0;
        let finalTaxTotal = parseFloat(order.tax_total) || 0;

        // Add preserved items totals
        finalSubtotal += preservedSubtotal;
        finalTaxTotal += preservedTax;

        // Calculate grand total based on tax option
        // In biyo-order, we need to check how to access this getter.
        // Based on live-staging it is global getters.isStoreTaxInclusive
        // We will try accessing it from global store getters.
        const isInclusive =
          this.$store.getters.isStoreTaxInclusive ||
          this.$store.getters["isStoreTaxInclusive"] ||
          false;

        const finalGrandTotal = calculateGrandTotal(
          finalSubtotal,
          finalTaxTotal,
          isInclusive
        );

        // Clone order and ensure all required fields match biyowebpos format
        const orderPayload = {
          ...order,
          // Use merged items
          items: mergedItems,

          // Core identifiers - id must be integer
          id: parseInt(order.id, 10),
          local_id: order.local_id || order.id?.toString(),
          number: order.number || order.id?.toString() || "0",

          // Status - 1 = OPEN (must be integer)
          status: parseInt(order.status, 10) || 1,

          // Timestamps
          open_date: order.open_date || order.created_at || now,
          created_at: order.created_at || order.open_date || now,
          updated_at: now,

          // Financial fields (ensure numbers)
          subtotal: finalSubtotal,
          tax_total: finalTaxTotal,
          grand_total: finalGrandTotal,
          total_sum: finalGrandTotal,
          discount_total: parseFloat(order.discount_total) || 0,
          order_discount: parseFloat(order.order_discount) || 0,
          discount_orders: parseFloat(order.discount_orders) || 0,

          // Table info - MUST be integer for biyowebpos filter comparison
          table: tableId,
          table_id: tableId,

          // Store info
          related_store: parseInt(order.related_store || this.getStore?.id, 10),

          // Order type - 1 = Online Order (must be integer)
          type: parseInt(order.type, 10) || 1,

          // Payments
          payments: order.payments || [],

          // Optional fields
          customer: order.customer || null,
          dining_option:
            diningOptionOverride?.name || order.dining_option || null,
          dining_option_type:
            diningOptionOverride?.id || order.dining_option_type || null,
          serve_at: order.serve_at || null,
          notes: order.notes || null,

          // Source for echo prevention
          source: liveObjectsService.getClientId()
        };

        // Publish to LiveObjects using map.set operation
        await liveObjectsService.publishOrderUpdate(orderPayload);
      } catch (error) {
        console.error(
          "[TableCheckout] ❌ Error publishing order to LiveObjects:",
          error
        );
      }
    },
    transformItemsForAPI(items) {
      return items.map((item) => {
        const modifiersTotal = item.modifiers
          ? item.modifiers.reduce((sum, mod) => sum + mod.price, 0)
          : 0;

        const subtotal = (item.price + modifiersTotal) * item.quantity;
        const taxAmount = (item.tax || 0) * item.quantity;
        const grandTotal = calculateGrandTotal(
          subtotal,
          taxAmount,
          this.isStoreTaxInclusive
        );

        return {
          product: item.product?.id || item.id,
          name: item.name || "",
          price: item.price || 0,
          quantity: item.quantity || 1,
          modifiers: item.modifiers || [],
          notes: item.notes || "",
          tax: item.tax || 0,
          tax_status: item.tax_status || 0,
          subtotal: subtotal,
          grand_total: grandTotal,
          cost: item.cost || item.price || 0,
          discount: 0,
          void_status: 0
        };
      });
    },
    async handleSubmit() {
      if (this.isSubmitting) {
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      if (!this.isAddItemsMode || this.newItems.length === 0) {
        return;
      }

      if (this.hasUnavailable86dItems()) {
        this.errors = {
          form: "One or more items in your cart are no longer available."
        };
        return;
      }

      this.isSubmitting = true;

      try {
        if (!this.$store.getters.baseUrl) {
          await this.initializeTableInfo();

          if (!this.$store.getters.baseUrl) {
            throw new Error(
              "Store base URL is not available. Please try again."
            );
          }
        }

        const baseUrl = this.$store.getters.baseUrl;
        if (baseUrl.includes("/table/") || baseUrl.includes("undefined")) {
          console.error("[TableCheckout] Invalid baseUrl detected:", baseUrl);
          const storeInfoResponse = await getStoreInfo(this.storeAlias);
          if (storeInfoResponse.ok) {
            this.storeInfo = await storeInfoResponse.json();
            await this.$store.dispatch(
              "setBaseUrl",
              this.storeInfo.dashboard_link
            );
          } else {
            throw new Error("Failed to fetch store information");
          }
        }

        const tableId = this.tableId || this.localTableId;
        const relatedStore = this.getStore?.id;

        if (!tableId) {
          throw new Error("Table ID is required");
        }

        if (!relatedStore) {
          throw new Error("Store ID is required");
        }

        const transformedItems = this.transformItemsForAPI(this.newItems);

        const diningOptionObj = this.$store.getters.availableDiningOptions.find(
          (opt) => opt.id === this.dining_option
        );
        const diningOptionName = diningOptionObj ? diningOptionObj.name : "";
        const diningOptionId = this.dining_option;

        const orderData = {
          table: tableId,
          related_store: relatedStore,
          items: transformedItems,
          dining_option: diningOptionName, // Send Name as dining_option
          dining_option_type: diningOptionId // Send ID as dining_option_type
        };

        const response = await createTableOrder(orderData);

        if (response.data && response.data.order) {
          const order = response.data.order;

          await this.$store.dispatch(
            "tableOrder/setCurrentOrderId",
            response.data.order.id
          );

          await this.$store.dispatch("tableOrder/setExistingOrder", {
            order: order,
            comparison: null,
            isUserSubmission: true
          });

          // Ensure LiveObjects is initialized before publishing. This is also
          // the only subscription a first-time order gets before the redirect
          // below lands back on TableOrder, so it needs the same callback -
          // otherwise a cancel/close landing in that narrow window updates
          // local state silently with no redirect or notification.
          if (!liveObjectsService.isReady()) {
            console.warn(
              "[TableCheckout] LiveObjects not ready, attempting to initialize..."
            );
            await this.$store.dispatch("tableOrder/initializeAbly", {
              onOrderUpdate: this.handleAblyOrderUpdate
            });
          }

          // Publish order update to LiveObjects so POS sees it in real-time
          await this.publishOrderToLiveObjects(response.data.order, {
            name: diningOptionName,
            id: diningOptionId
          });

          if (this.storeAlias && this.tableHash) {
            this.$router.push({
              name: "TableOrder",
              params: {
                storeAlias: this.storeAlias,
                tableHash: this.tableHash
              }
            });
          }
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        console.error("[TableCheckout] Error submitting table order:", error);

        if (await this.handleUnavailableItemsError(error)) {
          return;
        }

        const errorMessage =
          error?.data?.error ||
          error?.message ||
          "Failed to submit order. Please try again.";
        alert(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
    getUnavailableItemNames(error) {
      const items = [
        ...(Array.isArray(error?.data?.items) ? error.data.items : []),
        ...(Array.isArray(error?.data?.modifier_items)
          ? error.data.modifier_items
          : [])
      ];
      if (!items.length) {
        return [];
      }

      return items
        .map((message) =>
          String(message || "")
            .replace(/\s+is unavailable\.?$/i, "")
            .trim()
        )
        .filter(Boolean);
    },
    async handleUnavailableItemsError(error) {
      const unavailableNames = this.getUnavailableItemNames(error);
      if (error?.status !== 400 || unavailableNames.length === 0) {
        return false;
      }

      const unavailableNameSet = new Set(
        unavailableNames.map((name) => name.toLowerCase())
      );
      const itemsToRemove = (this.newItems || []).filter(
        (item) =>
          unavailableNameSet.has(String(item.name || "").toLowerCase()) ||
          (item.selectedModifiers || item.modifiers || []).some((modifier) =>
            unavailableNameSet.has(String(modifier?.name || "").toLowerCase())
          )
      );

      for (const item of itemsToRemove) {
        const productId = item.id || item.product?.id;
        if (productId) {
          this.$store.commit("store/SET_PRODUCT_86D", {
            productId,
            value: true
          });
        }
        if (item.cartId) {
          this.$store.dispatch("tableOrder/removeItemCart", item.cartId);
        }
      }

      this.errors = {
        form: `${unavailableNames.join(
          ", "
        )} is no longer available and has been removed from your cart.`
      };
      alert(this.errors.form);
      return true;
    }
  }
};
</script>
